import { Component } from '@theme/component';
import { ThemeEvents, CartAddEvent, CartUpdateEvent } from '@theme/events';
import { debounce, fetchConfig, isClickedOutside, onAnimationEnd } from '@theme/utilities';

const KIT_ITEMS_SECTION = 'kit-items';
const ITEMS_FETCH_PAGES = 2;
const FUZZY_THRESHOLD = 0.6;

/** Hidden line-item property prefix that stores each selected addon's handle.
 *  Used to round-trip selections on the edit-from-cart flow. */
const KIT_HANDLE_PROPERTY_PREFIX = '_kit_handle_';
const KIT_HANDLE_PROPERTY_REGEX = /^_kit_handle_\d+$/;

/** Hidden line-item property holding the selected addons' SKUs as a
 *  JSON-stringified array of strings, in slot order. */
const KIT_SKUS_PROPERTY = '_skus';

/**
 * Build-Your-Own-Kit component. Owns the trigger button + drawer dialog inside
 * the buy-buttons block. Coordinates with that block by setting a
 * `data-kit-state` attribute on `.buy-buttons-block` (and the moved-to-body
 * `<sticky-buy-button>` when present), and by injecting hidden
 * `properties[…]` inputs into the product form once all slots are full.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLDivElement} itemsContainer
 * @property {HTMLButtonElement} ctaButton
 * @property {HTMLInputElement} searchInput
 * @property {HTMLParagraphElement} noResults
 * @property {HTMLElement} liveRegion
 *
 * @extends Component<Refs>
 */
class KitDrawerComponent extends Component {
  requiredRefs = ['dialog', 'itemsContainer', 'ctaButton', 'searchInput', 'noResults', 'liveRegion'];

  #abortController = new AbortController();
  #previousScrollY = 0;
  /** @type {Promise<void>|null} */
  #itemsLoadPromise = null;
  /** @type {HTMLElement|null} */
  #buyBlock = null;
  /** @type {HTMLElement|null} */
  #stickyHost = null;
  /** @type {HTMLElement[]} */
  #counterElements = [];
  /** @type {HTMLElement[]} */
  #slotContainers = [];

  /** @type {{ productId?: string, productHandle?: string, currentVariantId?: string|number, currentKitSize?: number, variantKitSizes?: Record<string, number> }} */
  #config = {};
  #kitSize = 0;
  /** @type {Array<{handle:string,title:string,imageUrl:string,sku:string}|null>} */
  #selections = [];
  #activeIndex = 0;
  /** @type {string[]|null} */
  #presetHandles = null;
  /** Edit-from-cart state. Set by `#maybeHydrateFromCart` when the page loads
   *  with `?edit=true&key=…`. The capture-phase submit handler removes the old
   *  line, then re-submits; `#onCartUpdateAfterEdit` cleans up URL + state.
   *  @type {{lineKey:string}|null} */
  #editMode = null;
  /** Re-entry guard for `#onFormSubmitCapture` so our own `requestSubmit()`
   *  call doesn't trigger another removal attempt. */
  #bypassSubmitInterception = false;
  /** @type {HTMLFormElement|null} */
  #form = null;

  connectedCallback() {
    super.connectedCallback();
    const { signal } = this.#abortController;

    this.#config = this.#readConfig();
    this.#kitSize = Number(this.#config.currentKitSize) || 0;
    this.#selections = new Array(this.#kitSize).fill(null);

    // The trigger button(s) live inside the same buy-buttons-block — main trigger
    // next to the ATC plus an optional sticky-buy-button trigger.
    this.#buyBlock = this.closest('.buy-buttons-block');
    this.#form = this.#buyBlock?.querySelector('form[data-type="add-to-cart-form"]') ?? null;

    // sticky-buy-button.js reparents the popup to <body>, so we must find it
    // separately and mirror data-kit-state on it so the swap CSS still applies.
    // Use getAttribute('id') — `form.id` resolves to the <input name="id"> element.
    const formId = this.#form?.getAttribute('id');
    this.#stickyHost = formId
      ? document.querySelector(`sticky-buy-button[data-target-id="${CSS.escape(formId)}"]`)
      : null;

    this.#counterElements = [
      ...(this.#buyBlock?.querySelectorAll('[data-kit-counter]') || []),
      ...(this.#stickyHost?.querySelectorAll('[data-kit-counter]') || []),
    ];

    // Slot containers come from two places now: the drawer's own slots (inside
    // this component) and the standalone `kit-preview` block (placed anywhere
    // in the same section). Section-scope catches both and keeps them in sync.
    const sectionRoot = this.closest('.shopify-section');
    this.#slotContainers = Array.from(sectionRoot?.querySelectorAll('[data-kit-slots]') || []);

    // When a Bundle configurator (kit-preview) block is present, let its color
    // scheme drive the drawer too — otherwise the drawer keeps the scheme set on
    // the buy-buttons block.
    this.#applyPreviewColorScheme(sectionRoot);

    this.#renderSlots();
    this.#syncState();

    // variantUpdate bubbles up through the variant-picker → section path, which
    // doesn't include buy-buttons-block, so listen on the section root.
    const section = this.closest('.shopify-section');
    section?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });

    // Edit-from-cart: remove the old line BEFORE the new one is added. We MUST
    // listen at `window` in capture phase — `component.js` registers its submit
    // delegate on `document` with `{capture:true}`, and the capture chain runs
    // window → document → form, so a form-level capture listener would fire too
    // late (after handleSubmit has already kicked off /cart/add). Then
    // `cartUpdate` is used purely to strip the edit URL params once the add lands.
    window.addEventListener('submit', this.#onFormSubmitCapture, { capture: true, signal });
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdateAfterEdit, { signal });

    requestAnimationFrame(() => this.#maybeHydrateFromCart());
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    this.#buyBlock?.removeAttribute('data-kit-state');
    this.#stickyHost?.removeAttribute('data-kit-state');
    this.#clearFormInputs();
  }

  /**
   * Mirror the Bundle configurator (kit-preview) block's color scheme onto the
   * drawer dialog so both surfaces share one appearance control. Falls back to
   * the buy-buttons block's `kit_drawer_color_scheme` (rendered into the dialog
   * class server-side) when no Bundle configurator block is present.
   * @param {Element | null} sectionRoot
   */
  #applyPreviewColorScheme(sectionRoot) {
    const scheme = sectionRoot
      ?.querySelector('.kit-preview-block[data-kit-color-scheme]')
      ?.getAttribute('data-kit-color-scheme');
    const dialog = this.refs.dialog;
    if (!scheme || !dialog) return;

    for (const cls of [...dialog.classList]) {
      if (cls.startsWith('color-scheme-')) dialog.classList.remove(cls);
    }
    dialog.classList.add(`color-${scheme}`);
  }

  /* --------------------------------------------------------------------- *
   * State + visibility coordination                                        *
   * --------------------------------------------------------------------- */

  #readConfig() {
    const script = this.querySelector('script[data-kit-config]');
    if (!script || !script.textContent) return {};
    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      console.error('[kit-drawer] failed to parse config', error);
      return {};
    }
  }

  #syncState() {
    const filled = this.#selections.filter(Boolean).length;
    let state = 'inactive';
    if (this.#kitSize > 0) {
      state = filled === this.#kitSize ? 'complete' : 'incomplete';
    }

    this.#buyBlock?.setAttribute('data-kit-state', state);
    this.#stickyHost?.setAttribute('data-kit-state', state);

    const counterText = `(${Math.min(filled, this.#kitSize)}/${this.#kitSize})`;
    for (const counter of this.#counterElements) counter.textContent = counterText;

    if (state === 'complete') {
      this.#writeFormInputs();
    } else {
      this.#clearFormInputs();
    }

    this.#renderCta(state);
  }

  #clearFormInputs() {
    this.#form?.querySelectorAll('[data-kit-property]').forEach((el) => el.remove());
  }

  #writeFormInputs() {
    const form = this.#form;
    if (!form) return;

    this.#clearFormInputs();

    const itemLabel = this.#t('item') || 'Item';
    this.#selections.forEach((selection, i) => {
      if (!selection) return;

      const visibleInput = document.createElement('input');
      visibleInput.type = 'hidden';
      visibleInput.name = `properties[${itemLabel} ${i + 1}]`;
      visibleInput.value = selection.title;
      visibleInput.setAttribute('data-kit-property', '');
      form.appendChild(visibleInput);

      const hiddenInput = document.createElement('input');
      hiddenInput.type = 'hidden';
      hiddenInput.name = `properties[${KIT_HANDLE_PROPERTY_PREFIX}${i + 1}]`;
      hiddenInput.value = selection.handle;
      hiddenInput.setAttribute('data-kit-property', '');
      form.appendChild(hiddenInput);
    });

    const skus = this.#selections.filter(Boolean).map((selection) => selection.sku);
    const skuInput = document.createElement('input');
    skuInput.type = 'hidden';
    skuInput.name = `properties[${KIT_SKUS_PROPERTY}]`;
    skuInput.value = JSON.stringify(skus);
    skuInput.setAttribute('data-kit-property', '');
    form.appendChild(skuInput);
  }

  /* --------------------------------------------------------------------- *
   * Variant updates                                                        *
   * --------------------------------------------------------------------- */

  /** @param {import('@theme/events').VariantUpdateEvent} event */
  #onVariantUpdate = (event) => {
    const variantId = event.detail?.resource?.id;
    if (variantId == null) return;

    const lookup = this.#config.variantKitSizes || {};
    const newKitSize = Number(lookup[String(variantId)]) || 0;

    this.#config.currentVariantId = variantId;

    if (newKitSize === this.#kitSize) {
      // Size didn't change; keep selections.
      this.#syncState();
      return;
    }

    this.#kitSize = newKitSize;
    // Preserve existing picks across a resize; only pad/truncate to the new slot count.
    const preserved = this.#selections.slice(0, this.#kitSize);
    while (preserved.length < this.#kitSize) preserved.push(null);
    this.#selections = preserved;

    const nextEmpty = this.#selections.findIndex((s) => !s);
    this.#activeIndex = nextEmpty === -1 ? Math.max(this.#kitSize - 1, 0) : nextEmpty;

    this.#renderSlots();
    this.#paintSlots();
    this.#updateCardCounters();
    this.#syncState();
  };

  /* --------------------------------------------------------------------- *
   * Dialog lifecycle                                                       *
   * --------------------------------------------------------------------- */

  showDialog() {
    const { dialog } = this.refs;
    if (!dialog || dialog.open || this.#kitSize === 0) return;

    this.#previousScrollY = window.scrollY;

    requestAnimationFrame(() => {
      document.body.style.width = '100%';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${this.#previousScrollY}px`;

      dialog.showModal();
      this.addEventListener('click', this.#handleOutsideClick);
      dialog.addEventListener('cancel', this.#handleCancel);

      this.#loadItems();
    });
  }

  closeDialog = async () => {
    const { dialog } = this.refs;
    if (!dialog || !dialog.open) return;

    try {
      this.removeEventListener('click', this.#handleOutsideClick);
      dialog.removeEventListener('cancel', this.#handleCancel);

      dialog.classList.add('dialog-closing');
      await onAnimationEnd(dialog, undefined, { subtree: false });

      dialog.close();
      dialog.classList.remove('dialog-closing');

      document.body.style.width = '';
      document.body.style.position = '';
      document.body.style.top = '';
      window.scrollTo({ top: this.#previousScrollY, behavior: 'instant' });
    } catch (error) {
      dialog.classList.remove('dialog-closing');
      throw error;
    }
  };

  /** @param {MouseEvent} event */
  #handleOutsideClick = (event) => {
    if (isClickedOutside(event, this.refs.dialog)) this.closeDialog();
  };

  /** @param {Event} event */
  #handleCancel = (event) => {
    event.preventDefault();
    this.closeDialog();
  };

  /* --------------------------------------------------------------------- *
   * Slots                                                                  *
   * --------------------------------------------------------------------- */

  #renderSlots() {
    if (!this.#slotContainers.length) return;
    for (const container of this.#slotContainers) {
      container.replaceChildren();
      for (let i = 0; i < this.#kitSize; i++) {
        container.appendChild(this.#createSlotButton(i));
      }
    }
  }

  /** @param {number} i */
  #createSlotButton(i) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'kit-drawer__slot';
    button.dataset.kitSlot = String(i);
    // Use the #id form so the dispatch works even from slot buttons that live
    // outside the kit-drawer-component element (e.g. the inline preview).
    button.setAttribute('on:click', `#${this.id}/focusSlot`);
    if (i === this.#activeIndex) button.setAttribute('data-active', '');

    const image = document.createElement('span');
    image.className = 'kit-drawer__slot-image';

    const clear = document.createElement('span');
    clear.className = 'kit-drawer__slot-clear';
    clear.setAttribute('on:click', `#${this.id}/clearSlot`);
    clear.setAttribute('aria-label', this.#t('clear_slot') || 'Remove');
    clear.innerHTML =
      '<svg viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">' +
      '<path d="M12 2L2 12" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round" stroke-linejoin="round"/>' +
      '<path d="M12 12L2 2" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round" stroke-linejoin="round"/>' +
      '</svg>';
    image.appendChild(clear);

    const label = document.createElement('span');
    label.className = 'kit-drawer__slot-label';
    label.textContent = this.#slotLabel(i);

    button.append(image, label);
    return button;
  }

  /** @param {Event & { target: HTMLElement }} event */
  focusSlot(event) {
    const button = event.target.closest('[data-kit-slot]');
    if (!button) return;
    const index = Number(button.dataset.kitSlot);
    if (Number.isNaN(index)) return;

    this.#activeIndex = index;
    this.#paintSlots();

    // If the click came from the inline preview (outside the dialog), open the
    // drawer so the shopper can keep editing.
    if (!this.refs.dialog?.contains(button)) {
      this.showDialog();
    }
  }

  /** @param {Event & { target: HTMLElement }} event */
  clearSlot(event) {
    event.stopPropagation();
    const slotButton = event.target.closest('[data-kit-slot]');
    if (!slotButton) return;
    const index = Number(slotButton.dataset.kitSlot);
    if (Number.isNaN(index)) return;

    this.#selections[index] = null;
    // Focus the cleared slot so the next addon click lands here.
    this.#activeIndex = index;
    this.#paintSlots();
    this.#updateCardCounters();
    this.#syncState();
  }

  clearAll() {
    this.#selections = new Array(this.#kitSize).fill(null);
    this.#activeIndex = 0;
    this.#paintSlots();
    this.#updateCardCounters();
    this.#syncState();
  }

  #paintSlots() {
    for (const container of this.#slotContainers) {
      container.querySelectorAll('[data-kit-slot]').forEach((button, i) => {
        const selection = this.#selections[i];
        button.toggleAttribute('data-active', i === this.#activeIndex);
        button.toggleAttribute('data-filled', !!selection);

        const image = button.querySelector('.kit-drawer__slot-image');
        if (image instanceof HTMLElement) {
          image.style.backgroundImage = selection?.imageUrl ? `url(${selection.imageUrl})` : '';
        }
        const label = button.querySelector('.kit-drawer__slot-label');
        if (label) label.textContent = selection?.title || this.#slotLabel(i);
      });
    }
  }

  #slotLabel(i) {
    return `${this.#t('item') || 'Item'} ${i + 1}`;
  }

  /* --------------------------------------------------------------------- *
   * Items: lazy load + search + preset filter                             *
   * --------------------------------------------------------------------- */

  /**
   * Lazy-load the products grid. Returns a Promise that resolves only once items
   * are actually in the DOM — callers (like edit-mode hydration) can `await` it.
   */
  #loadItems() {
    if (this.#itemsLoadPromise) return this.#itemsLoadPromise;

    this.#itemsLoadPromise = (async () => {
      const { itemsContainer } = this.refs;

      try {
        const pages = await Promise.all(
          Array.from({ length: ITEMS_FETCH_PAGES }, (_, i) =>
            fetch(`?section_id=${KIT_ITEMS_SECTION}&page=${i + 1}`).then((r) => r.text()),
          ),
        );

        const parser = new DOMParser();
        const fragments = pages
          .map((html) => parser.parseFromString(html, 'text/html').querySelector('[data-kit-items]'))
          .filter((el) => el instanceof HTMLElement);

        if (!fragments.length) {
          itemsContainer.querySelector('[data-kit-items-placeholder]')?.remove();
          return;
        }

        const merged = fragments[0];
        for (let i = 1; i < fragments.length; i++) {
          merged.append(...fragments[i].childNodes);
        }

        itemsContainer.replaceChildren(merged);
        this.#applyFilters();
        this.#updateCardCounters();
      } catch (error) {
        this.#itemsLoadPromise = null;
        console.error('[kit-drawer]', error);
      }
    })();

    return this.#itemsLoadPromise;
  }

  /** @param {Event & { target: HTMLElement }} event */
  selectAddon(event) {
    const card = event.target.closest('[data-kit-card]');
    if (!(card instanceof HTMLElement)) return;
    if (card.dataset.soldOut === 'true') return;
    if (!this.#kitSize) return;

    const activeIndex = this.#activeIndex;
    if (activeIndex >= this.#kitSize) return;

    this.#selections[activeIndex] = this.#selectionFromCard(card);

    const filledCount = this.#selections.filter(Boolean).length;
    const nextEmpty = this.#selections.findIndex((s) => !s);
    this.#activeIndex = nextEmpty === -1 ? this.#kitSize - 1 : nextEmpty;

    this.#paintSlots();
    this.#updateCardCounters();
    this.#syncState();
    this.#announce(`${this.#t('item') || 'Item'} ${Math.min(filledCount, this.#kitSize)}/${this.#kitSize}`);
  }

  #updateCardCounters() {
    const counts = new Map();
    for (const selection of this.#selections) {
      if (!selection) continue;
      counts.set(selection.handle, (counts.get(selection.handle) || 0) + 1);
    }

    const cards = this.refs.itemsContainer?.querySelectorAll('[data-kit-card]') || [];
    cards.forEach((card) => {
      const counter = card.querySelector('[data-kit-card-counter]');
      if (!counter) return;
      const count = counts.get(card.dataset.productHandle) || 0;
      counter.textContent = String(count);
      counter.toggleAttribute('hidden', count === 0);
    });
  }

  /** @param {Event & { target: HTMLElement }} event */
  selectPreset(event) {
    const button = event.target.closest('[data-kit-preset]');
    if (!(button instanceof HTMLElement)) return;

    const presetsContainer = button.parentElement;
    presetsContainer?.querySelectorAll('[data-kit-preset]').forEach((b) => b.removeAttribute('data-active'));
    button.setAttribute('data-active', '');

    const raw = button.dataset.kitPreset || '';
    this.#presetHandles = raw ? JSON.parse(raw) : null;
    this.#applyFilters();
  }

  /** @param {Event & { target: HTMLInputElement }} event */
  handleSearch = (event) => {
    this.#queueSearch(event.target.value);
  };

  #queueSearch = debounce((value) => {
    this.#applyFilters(value);
  }, 150);

  /** @param {KeyboardEvent} event */
  preventSubmit(event) {
    if (event.key === 'Enter') event.preventDefault();
  }

  /** @param {string} [searchOverride] */
  #applyFilters(searchOverride) {
    const query = (searchOverride ?? this.refs.searchInput?.value ?? '').toLowerCase().trim();
    const presetHandles = this.#presetHandles;

    let visible = 0;
    const cards = this.refs.itemsContainer?.querySelectorAll('[data-kit-card]') || [];

    cards.forEach((card) => {
      const handle = card.dataset.productHandle || '';
      const title = card.dataset.productTitle || '';
      const terms = card.dataset.searchTerms || '';

      const passesPreset = !presetHandles || presetHandles.includes(handle);
      const passesSearch = query === '' || fuzzyMatch(`${title} ${terms}`, query);

      const visibleCard = passesPreset && passesSearch;
      card.toggleAttribute('hidden', !visibleCard);
      if (visibleCard) visible++;
    });

    this.refs.noResults?.toggleAttribute('hidden', visible !== 0);
  }

  /* --------------------------------------------------------------------- *
   * Submit (drawer CTA path)                                              *
   * --------------------------------------------------------------------- */

  /** @param {'inactive'|'incomplete'|'complete'} state */
  #renderCta(state) {
    const { ctaButton } = this.refs;
    if (!ctaButton) return;

    const complete = state === 'complete';
    ctaButton.disabled = !complete;
    const textEl = ctaButton.querySelector('[data-kit-cta-text]');
    if (textEl) {
      textEl.textContent = complete
        ? this.#t('review_kit') || 'Review my kit'
        : this.#t('select_next') || 'Select an item';
    }
  }

  /* --------------------------------------------------------------------- *
   * Edit-from-cart hydration                                              *
   * --------------------------------------------------------------------- */

  async #maybeHydrateFromCart() {
    const params = new URLSearchParams(window.location.search);
    if (params.get('edit') !== 'true') return;

    const lineKey = params.get('key');
    if (!lineKey) return;

    try {
      // Prefer the URL params we emit from cart-products.liquid — when a cart
      // transform bundles the line, the wrapper's own .properties is empty and
      // the kit handles live inside item_components (not exposed via /cart.js).
      let handles = (params.get('kit_handles') || '')
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);

      if (!handles.length) {
        const response = await fetch(`${Theme.routes.cart_url}.js`);
        const cart = await response.json();
        const line = cart.items.find((item) => item.key === lineKey);
        if (!line) return;

        handles = Object.entries(line.properties || {})
          .filter(([key]) => KIT_HANDLE_PROPERTY_REGEX.test(key))
          .sort(
            ([a], [b]) =>
              Number(a.replace(KIT_HANDLE_PROPERTY_PREFIX, '')) - Number(b.replace(KIT_HANDLE_PROPERTY_PREFIX, '')),
          )
          .map(([, value]) => String(value));
      }

      if (!handles.length) return;

      this.#editMode = { lineKey };
      this.showDialog();
      await this.#loadItems();
      this.#hydrateSelections(handles);
    } catch (error) {
      console.error('[kit-drawer]', error);
    }
  }

  /**
   * Capture-phase submit handler. In edit mode, removes the old kit line
   * BEFORE letting the normal ATC flow add the new one — so the cart never
   * momentarily shows both. On removal failure we still proceed with ATC so
   * the user's click isn't silently dropped.
   *
   * @param {SubmitEvent} event
   */
  #onFormSubmitCapture = async (event) => {
    if (this.#bypassSubmitInterception) return;
    if (!this.#editMode?.lineKey) return;
    if (event.target !== this.#form) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    const submitter = /** @type {HTMLElement | null} */ (event.submitter);

    // Set the bypass flag synchronously before awaiting the removal so a second
    // submit fired during the network round-trip (double-click, programmatic)
    // can't pass the guard and trigger a duplicate /cart/change.js call.
    this.#bypassSubmitInterception = true;
    try {
      await this.#removeOldKitLine();
      this.#form?.requestSubmit(submitter);
    } finally {
      this.#bypassSubmitInterception = false;
    }
  };

  /**
   * Remove the old kit line entirely (qty=0). On failure we log and fall
   * through so the user's ATC click isn't dropped.
   */
  async #removeOldKitLine() {
    if (!this.#editMode?.lineKey) return;
    const { lineKey } = this.#editMode;

    const cartSectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : null))
      .filter(Boolean);

    try {
      const response = await fetch(Theme.routes.cart_change_url, {
        ...fetchConfig('json', {
          body: JSON.stringify({
            id: lineKey,
            quantity: 0,
            sections: cartSectionIds.join(','),
          }),
        }),
      });
      if (!response.ok) throw new Error(`Cart change failed: ${response.status}`);
      const result = await response.json();

      document.dispatchEvent(
        new CartUpdateEvent(result, this.id, {
          source: 'kit-drawer-edit-replace',
          sections: result.sections,
        }),
      );
    } catch (error) {
      console.error('[kit-drawer] failed to remove old kit line:', error);
    }
  }

  /**
   * After the new line lands in the cart, clear edit-mode state and strip
   * `?edit=…&key=…` from the URL so a refresh won't re-enter edit mode.
   *
   * @param {Event} event
   */
  #onCartUpdateAfterEdit = (event) => {
    if (!this.#editMode?.lineKey) return;
    if (!(event instanceof CartAddEvent)) return;
    if (event.detail?.data?.didError) return;

    const eventProductId = String(event.detail?.data?.productId || '');
    const currentProductId = String(this.#config.productId || '');
    if (currentProductId && eventProductId && eventProductId !== currentProductId) return;

    this.#editMode = null;
    this.#cleanupEditUrl();
  };

  #cleanupEditUrl() {
    const url = new URL(window.location.href);
    const editParams = ['edit', 'key', 'kit_handles', 'addon_variants'];
    if (!editParams.some((p) => url.searchParams.has(p))) return;
    for (const p of editParams) url.searchParams.delete(p);
    history.replaceState(history.state, '', url.toString());
  }

  /** @param {string[]} handles */
  #hydrateSelections(handles) {
    const cards = Array.from(this.refs.itemsContainer?.querySelectorAll('[data-kit-card]') || []);

    handles.slice(0, this.#kitSize).forEach((handle, index) => {
      const card = cards.find((c) => c.dataset.productHandle === handle);
      if (card) this.#selections[index] = this.#selectionFromCard(card);
    });

    const nextEmpty = this.#selections.findIndex((s) => !s);
    this.#activeIndex = nextEmpty === -1 ? this.#kitSize - 1 : nextEmpty;

    this.#paintSlots();
    this.#updateCardCounters();
    this.#syncState();
  }

  /* --------------------------------------------------------------------- *
   * Helpers                                                                *
   * --------------------------------------------------------------------- */

  /** Build a selection record from a `[data-kit-card]` element. Used by both
   *  fresh shopper picks and edit-from-cart hydration. The `currentSrc || src`
   *  fallback covers cards where the image has loaded vs. where it hasn't.
   *  @param {HTMLElement} card */
  #selectionFromCard(card) {
    const image = card.querySelector('.kit-card__image');
    return {
      handle: card.dataset.productHandle || '',
      title: card.dataset.productTitle || '',
      imageUrl: image instanceof HTMLImageElement ? image.currentSrc || image.src : '',
      sku: card.dataset.productSku || '',
    };
  }

  /** @param {string} key */
  #t(key) {
    return /** @type {any} */ (window).Theme?.translations?.kit?.[key];
  }

  /** @param {string} text */
  #announce(text) {
    if (this.refs.liveRegion) this.refs.liveRegion.textContent = text;
  }
}

if (!customElements.get('kit-drawer-component')) {
  customElements.define('kit-drawer-component', KitDrawerComponent);
}

/* ===================================================================== *
 * Fuzzy matching                                                         *
 * ===================================================================== */

function fuzzyMatch(text, query) {
  if (!text || !query) return false;
  const t = text.toLowerCase();
  const q = query.toLowerCase();

  if (t.includes(q)) return true;
  if (/\d/.test(q)) return false;

  if (similarity(t, q) >= FUZZY_THRESHOLD) return true;

  const textWords = t.split(/\s+/);
  return q
    .split(/\s+/)
    .every((word) => textWords.some((tw) => tw.includes(word) || similarity(tw, word) >= FUZZY_THRESHOLD));
}

/** Normalised Levenshtein similarity in [0, 1]. */
function similarity(a, b) {
  const m = a.length;
  const n = b.length;
  if (!m || !n) return 0;

  /** @type {number[][]} */
  const matrix = Array.from({ length: m + 1 }, (_, i) => [i]);
  for (let j = 0; j <= n; j++) matrix[0][j] = j;

  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      matrix[i][j] = Math.min(matrix[i - 1][j] + 1, matrix[i][j - 1] + 1, matrix[i - 1][j - 1] + cost);
    }
  }

  return 1 - matrix[m][n] / Math.max(m, n);
}
